import { Heading, Text, Button } from "@radix-ui/themes";
import { useState } from "react";
import { FiExternalLink, FiEdit2 } from "react-icons/fi";
import { FaHome } from "react-icons/fa";
import {
  FaFacebook,
  FaInstagram,
  FaSoundcloud,
  FaTiktok,
  FaXTwitter,
  FaYoutube,
} from "react-icons/fa6";
import { panelTokens, linkRowTokens } from "@/lib/tokens";
import type { CreatorChannelDetailDto } from "@/lib/lutely-api/gen/dtos";
import { ensureHttpsUrl } from "@/lib/utils";

type LinksSectionProps = {
  links: CreatorChannelDetailDto[] | null;
  canEdit?: boolean;
  onAddLinks?: () => void;
  artistColor?: string;
};

// Icon mapping for social media channels
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  facebook: FaFacebook,
  instagram: FaInstagram,
  tiktok: FaTiktok,
  youtube: FaYoutube,
  soundcloud: FaSoundcloud,
  twitter: FaXTwitter,
  x: FaXTwitter,
  homepage: FaHome,
};

function getSocialIcon(channelCode: string, color?: string) {
  const IconComponent = iconMap[channelCode.toLowerCase()];
  const iconClassName = "w-4 h-4 flex-shrink-0";

  const icon = IconComponent ? (
    <IconComponent className={iconClassName} />
  ) : (
    <FiExternalLink className={iconClassName} />
  );

  if (color) {
    return (
      <span className="inline-flex" style={{ color }}>
        {icon}
      </span>
    );
  }

  return icon;
}

function LinkRow({
  label,
  href,
  channelCode,
  artistColor,
}: {
  label: string;
  href: string;
  channelCode: string;
  artistColor?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const hoverColor = isHovered && artistColor ? artistColor : undefined;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${linkRowTokens.row} group hover:bg-transparent`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span className="flex items-center gap-2 transition-colors duration-200">
        {getSocialIcon(channelCode, hoverColor)}
        <span
          className={`${linkRowTokens.text} transition-colors duration-200`}
          style={hoverColor ? { color: hoverColor } : undefined}
        >
          {label}
        </span>
      </span>
    </a>
  );
}

export function LinksSection({
  links,
  canEdit = false,
  onAddLinks,
  artistColor,
}: LinksSectionProps) {
  // Filter links by normalized URL value to exclude whitespace-only URLs
  const validLinks =
    links
      ?.map((link) => ({
        ...link,
        href: ensureHttpsUrl(link.url),
      }))
      .filter((link) => link.href) || [];

  return (
    <div>
      <Heading
        as="h3"
        size="2"
        className={`${panelTokens.heading} text-sm font-semibold leading-[140%]`}
        style={artistColor ? { color: artistColor } : { color: "#050C2B" }}
      >
        Links
      </Heading>

      {validLinks.length > 0 ? (
        <div className="grid grid-cols-2 gap-1">
          {validLinks.map((link) => (
            <LinkRow
              key={`${link.channel.code}-${link.url || ""}`}
              label={link.channel.name}
              href={link.href}
              channelCode={link.channel.code}
              artistColor={artistColor}
            />
          ))}
        </div>
      ) : canEdit && onAddLinks ? (
        <Button
          variant="ghost"
          onClick={onAddLinks}
          className="text-sm font-normal leading-[140%] text-[#525966] hover:text-black flex items-center gap-2"
        >
          <FiEdit2 className="w-3 h-3" />
          <span>Add Links</span>
        </Button>
      ) : (
        <Text className="text-sm font-normal leading-[140%] text-[#525966]">
          No links added.
        </Text>
      )}
    </div>
  );
}

export default LinksSection;
